import {
  Alert,
  Button,
  CloseButton,
  Drawer,
  Group,
  Skeleton,
  Stack,
  Text,
} from '@mantine/core';
import { isMobile } from 'react-device-detect';
import {
  useCloseRateCompanyModal,
  useOpenRequestRatingModal,
  useRateCompanyModal,
} from '@worker/stores/workerStore';
import { useJobAssignment } from '@worker/hooks/useJobAssignment';
import RateCompanyForm from './RateCompanyForm';

/**
 * Worker-side drawer for rating a company.
 *
 * At this moment this is used only on Worker inbox, if needed somewhere else
 * change the the place where this is rendered to a higher level (WorkerAppShell or similar).
 */
export default function RateCompanyDrawer() {
  const { isOpen, jobAssignmentId } = useRateCompanyModal();
  const closeModal = useCloseRateCompanyModal();
  const openRequestRatingModal = useOpenRequestRatingModal();

  const jobAssignmentQuery = useJobAssignment(jobAssignmentId, {
    enabled: isOpen,
  });

  const handleRated = () => {
    const jobAssignment = jobAssignmentQuery.data;
    closeModal();
    if (jobAssignment?.canAskForRating) {
      openRequestRatingModal({
        jobAssignmentId: jobAssignment.id,
        isValidatedCompany: jobAssignment.isValidatedCompany,
      });
    }
  };

  const hasId = jobAssignmentId != null;
  const isLoading = hasId && jobAssignmentQuery.isPending;
  const hasFailed =
    hasId &&
    !jobAssignmentQuery.isPending &&
    (jobAssignmentQuery.isError || !jobAssignmentQuery.data);

  return (
    <Drawer
      opened={isOpen}
      onClose={closeModal}
      position="right"
      size={isMobile ? '100%' : 'xl'}
      padding={0}
      withCloseButton={false}
    >
      <CloseButton
        onClick={closeModal}
        aria-label="Close drawer"
        size="lg"
        style={{ position: 'absolute', top: 12, right: 12, zIndex: 1 }}
      />
      {/* Empty state — drawer opened without a job assignment id */}
      {!hasId && (
        <Stack p="md">
          <Text c="dimmed">No rating data available.</Text>
        </Stack>
      )}

      {isLoading && (
        <Stack p="md" gap="md">
          <Group>
            <Skeleton height={48} width={48} radius="md" />
            <Stack gap={4} style={{ flex: 1 }}>
              <Skeleton height={16} width="60%" />
              <Skeleton height={12} width="40%" />
            </Stack>
          </Group>
          <Skeleton height={80} />
          <Skeleton height={120} />
          <Skeleton height={80} />
        </Stack>
      )}

      {hasFailed && (
        <Stack p="md">
          <Alert color="red" title="Could not load rating data">
            We were unable to load this rating. Please try again.
          </Alert>
          <Button
            variant="light"
            onClick={() => jobAssignmentQuery.refetch()}
            loading={jobAssignmentQuery.isFetching}
          >
            Retry
          </Button>
        </Stack>
      )}

      {jobAssignmentQuery.data && (
        <RateCompanyForm
          jobAssignment={jobAssignmentQuery.data}
          onSuccess={handleRated}
          onCancel={closeModal}
        />
      )}
    </Drawer>
  );
}
